import { supabase, isDemoMode } from '@/lib/supabase';

/** Send a welcome notification after registration */
export async function sendWelcomeNotification(
  userId: string,
  orgId: string | null,
  userName: string
) {
  if (isDemoMode) return;

  try {
    await supabase.from('notifications').insert({
      user_id: userId,
      organization_id: orgId,
      title: 'Welcome to Tickally! 🎉',
      message: `Hi ${userName}, welcome aboard! You're all set to manage your organization. Explore the dashboard to get started.`,
      type: 'success',
    });
  } catch (err) {
    console.error('Error sending welcome notification:', err);
  }
}

/** Send a member limit warning (at 80% and at 100% of the plan limit) */
export async function sendMemberLimitNotification(
  userId: string,
  orgId: string,
  currentCount: number,
  maxAllowed: number
) {
  if (isDemoMode) return;

  const isAtLimit = currentCount >= maxAllowed;
  const title = isAtLimit ? 'Member Limit Reached' : 'Member Limit Warning';
  const type = isAtLimit ? 'error' : 'warning';
  const message = isAtLimit
    ? `You've reached your member limit (${currentCount}/${maxAllowed}). New members cannot be added until you upgrade your plan.`
    : `You've used ${Math.round((currentCount / maxAllowed) * 100)}% of your member limit (${currentCount}/${maxAllowed}). Consider upgrading your plan to add more members.`;

  try {
    await supabase.from('notifications').insert({
      user_id: userId,
      organization_id: orgId,
      title,
      message,
      type,
    });
  } catch (err) {
    console.error('Error sending member limit notification:', err);
  }
}

/** Send an app-wide announcement to all users of an organization */
export async function sendOrgAnnouncement(
  orgId: string,
  title: string,
  message: string
) {
  if (isDemoMode) return;

  try {
    // Get all users in the organization
    const { data: members, error } = await supabase
      .from('user_organizations')
      .select('user_id')
      .eq('organization_id', orgId)
      .eq('is_active', true);

    if (error || !members) return;

    const notifications = members.map(({ user_id }) => ({
      user_id,
      organization_id: orgId,
      title,
      message,
      type: 'announcement' as const,
    }));

    if (notifications.length > 0) {
      await supabase.from('notifications').insert(notifications);
    }
  } catch (err) {
    console.error('Error sending org announcement:', err);
  }
}
